import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {VwuiModalConfig, VwuiModalRef} from '@bvgo-ict/vwui-angular';
import {ProjectRelation} from '../../../models/project-relation';
import {patternIgnoreSpaces} from '../../../utils/custom-validators';
import {distinctUntilChanged, map, shareReplay, startWith} from 'rxjs/operators';
import {ProjectRelationService} from '../../../services/project-relation.service';
import {ToastrService} from 'ngx-toastr';
import {combineLatest, Subscription} from 'rxjs';
import {AuthService} from '../../../services/auth.service';
import {Authority} from '../../../models/authority';

@Component({
    selector: 'app-prd-relation-modal',
    templateUrl: './prd-relation-modal.component.html',
    standalone: false
})
export class PrdRelationModalComponent implements OnInit, OnDestroy {
    private projectRelationService = inject<ProjectRelationService>('ProjectRelationService' as any);
    private authService = inject<AuthService>('AuthService' as any);
    private modalConfig = inject<VwuiModalConfig<{
    refId: number;
    relation?: ProjectRelation;
}>>(VwuiModalConfig);
    private toast = inject(ToastrService);
    modalRef = inject(VwuiModalRef);

    roleControl = new UntypedFormControl(null, Validators.required);
    companyControl = new UntypedFormControl(null, Validators.required);
    companyFieldsForm = new UntypedFormGroup({
        companyName: new UntypedFormControl(null, Validators.required),
        street: new UntypedFormControl(null, [Validators.required, Validators.maxLength(255)]),
        postalCode: new UntypedFormControl(null, [Validators.required, patternIgnoreSpaces(/^[0-9]{4}[A-Z]{2}$/)]),
        city: new UntypedFormControl(null, [Validators.required, Validators.maxLength(255)]),
        phoneNumber: new UntypedFormControl(null, [Validators.required, patternIgnoreSpaces(/^([0-9]{10}|\+[0-9]{11})$/)]),
    });
    contactPersonForm = new UntypedFormGroup({
        contactPersonRole: new UntypedFormControl(null, [Validators.required, Validators.maxLength(255)]),
        contactPersonName: new UntypedFormControl(null, [Validators.required, Validators.maxLength(255)]),
    });

    formValid$ = combineLatest([
        this.roleControl.statusChanges,
        this.companyControl.statusChanges,
        this.companyFieldsForm.statusChanges,
        this.contactPersonForm.statusChanges
    ]).pipe(
        map(([roleControlStatus, companyControlStatus, companyFieldsStatus, contactPersonStatus]) => {
            return [roleControlStatus, companyControlStatus, contactPersonStatus].every(it => it === 'VALID') &&
                (companyFieldsStatus === 'VALID' || companyFieldsStatus === 'DISABLED');
        }),
        startWith(false),
        shareReplay(1)
    );

    involvedVWCompanyRole = 'InvolvedVWCompany'
    involvedVWCompanySelected$ = this.roleControl.valueChanges.pipe(
        map(role => role?.key === this.involvedVWCompanyRole),
        distinctUntilChanged()
    );
    relationId: number | undefined;
    vwCompanyRoleSelected = false;
    existingNonVWCompanySelected = false;
    saving = false;
    isAdmin = false;
    vwCompanyInfoLabel = "Deze relatie gegevens worden automatisch ingeladen uit Navision. " +
        "Mocht u de gegevens aan willen passen, kunt u contact opnemen met de PRS admin";
    existingNonVWCompanyLabel = "Mocht u de relatie gegevens aan willen passen, kunt u contact opnemen met de PRS admin";

    private subscriptions: Subscription[] = [];

    ngOnInit() {
        this.authService.authorities$.subscribe(authorities => {
            this.isAdmin = authorities.some(authority => authority == Authority.admin) ? true : false;
        }),
        this.subscriptions.push(
            this.companyControl.valueChanges.subscribe(selected => {
                if (selected != null) { // company value entered
                    this.projectRelationService.customCompanyByCompanyName(selected.companyName).subscribe(async value => {
                        if ( (value != null && !this.isAdmin) || this.vwCompanyRoleSelected) {
                            this.existingNonVWCompanySelected = true;
                            this.companyFieldsForm.disable();
                        } else {
                            this.existingNonVWCompanySelected = false;
                            this.companyFieldsForm.enable();
                        }
                    })
                } else {  // no value entered
                    this.existingNonVWCompanySelected = false;
                    if (!this.vwCompanyRoleSelected) {
                        this.companyFieldsForm.enable();
                    }
                    this.companyFieldsForm.reset();
                }
                this.companyFieldsForm.patchValue(selected);
            }),
            this.involvedVWCompanySelected$.subscribe(selected => {
                this.vwCompanyRoleSelected = selected;
                this.companyFieldsForm.reset();
                this.companyControl.reset();
                if (selected || (!selected && !this.isAdmin && this.existingNonVWCompanySelected)) {
                    this.companyFieldsForm.disable();
                } else {
                    this.companyFieldsForm.enable();
                }
            }),
            this.formValid$.subscribe()
        );

        if (this.modalConfig.data?.relation) {
            const {id, role, companyName, companyKvwsNumber, ...rest} = this.modalConfig.data?.relation;

            this.relationId = id;

            this.roleControl.patchValue(role);
            this.companyControl.patchValue({id: companyKvwsNumber, companyName});
            this.companyFieldsForm.patchValue(rest);
            this.contactPersonForm.patchValue(rest);

            if (companyKvwsNumber) {
                this.companyFieldsForm.disable();
            }
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(it => it.unsubscribe());
    }

    get refId() {
        return this.modalConfig.data!.refId!;
    }

    async save() {
        if (this.relationId) {
            await this.updateRelation();
        } else {
            await this.createRelation();
        }
    }

    private getFormDataAsProjectRelationVw() {
        return {
            id: this.relationId,
            role: this.roleControl.value.key,
            mdmCompany: this.companyControl.value.id,
            ...this.contactPersonForm.value
        };
    }

    private getFormDataAsProjectRelationOther() {
        return {
            id: this.relationId,
            role: this.roleControl.value.key,
            companyName: this.companyControl.value.companyName,
            ...this.companyFieldsForm.value,
            postalCode: this.companyFieldsForm.value.postalCode.toUpperCase().replace(/\s/g,''),
            ...this.contactPersonForm.value
        };
    }

    private async createRelation() {
        if (this.saving) {
            return;
        }

        this.saving = true;

        try {
            let result;
            if (this.vwCompanyRoleSelected) {
                result = await this.projectRelationService.createVw(this.refId, this.getFormDataAsProjectRelationVw()).toPromise()
            } else {
                result = await this.projectRelationService.createOther(this.refId, this.getFormDataAsProjectRelationOther()).toPromise()
            }

            this.modalRef.close(result);
            this.toast.success('Projectrelatie aangemaakt.');
        } catch (error) {
            console.error(error);
            this.toast.error('Projectrelatie kon niet aangemaakt worden.');
        } finally {
            this.saving = false;
        }
    }

    private async updateRelation() {
        if (this.saving) {
            return;
        }

        this.saving = true;

        try {
            let result;
            if (this.vwCompanyRoleSelected) {
                result = await this.projectRelationService.updateVw(this.refId, this.getFormDataAsProjectRelationVw()).toPromise()
            } else {
                result = await this.projectRelationService.updateOther(this.refId, this.getFormDataAsProjectRelationOther()).toPromise()
            }

            this.modalRef.close(result);
            this.toast.success('Projectrelatie bijgewerkt.');
        } catch (error) {
            console.error(error);
            this.toast.error('Projectrelatie kon niet bijgewerkt worden.');
        } finally {
            this.saving = false;
        }
    }
}
